// The Help button on every page, and the drawer it opens. Help always shows the
// articles for the page you're on first; the drawer's code loads only when it's
// first opened, so the button costs a cheap phone almost nothing.
import { lazy, Suspense, useEffect, useState } from "react";
import type { HelpSource } from "../lib/help.ts";
import type { OpenHelp } from "../lib/open-help.ts";
import { openHelp } from "../lib/open-help.ts";

/** Below the page's own content and not needed to use it, so it loads after the page. */
export const HelpFirstVisit = lazy(() => import("./help-first-visit.tsx").then((m) => ({ default: m.HelpFirstVisit })));

// Fetched as soon as a finger or pointer reaches the button, so it's usually there by the tap.
const loadDrawer = () => import("./help-drawer.tsx");
const warmUp = () => void loadDrawer();
const HelpDrawer = lazy(() => loadDrawer().then((m) => ({ default: m.HelpDrawer })));

/** A labelled Help button. On navy (headers and the sidebar) it's mint, like the other header controls. */
/** A control in the navy phone header (Help, Find): narrow phones put the word under the icon, like the tab bar, so the agency name keeps its room. */
export const headerControlClass =
	"inline-flex min-h-12 min-w-12 shrink-0 flex-col items-center justify-center rounded-xl px-1.5 text-sm leading-none font-semibold text-mint hover:bg-white/10 sm:flex-row sm:gap-1.5 sm:rounded-full sm:border-2 sm:border-mint sm:px-3 sm:text-base";

export function HelpButton({ look = "header" }: { look?: "header" | "sidebar" | "plain" }) {
	const style = {
		header: headerControlClass,
		sidebar:
			"flex min-h-11 w-full items-center gap-3 rounded-xl border border-white/25 px-3 text-left font-semibold text-white/85 hover:bg-white/10",
		plain:
			"inline-flex min-h-12 items-center gap-1.5 rounded-full border-2 border-b-4 border-navy bg-card px-4 font-semibold text-ink active:border-b-2 dark:border-mint",
	}[look];
	return (
		<button
			type="button"
			onClick={() => openHelp()}
			onPointerEnter={warmUp}
			onTouchStart={warmUp}
			onFocus={warmUp}
			className={style}
		>
			{/* Drawn in CSS, not an icon file: this button is on every candidate and client page. */}
			<span
				aria-hidden="true"
				className={`inline-flex size-[1.375rem] shrink-0 items-center justify-center rounded-full border-2 border-current font-display text-sm font-bold ${look === "plain" ? "text-purple" : "text-mint"}`}
			>
				?
			</span>
			<span>Help</span>
		</button>
	);
}

/** Listens for openHelp() and shows the drawer for the current page. Mount once per app. */
export function HelpHost({
	source,
	pageKey,
	go,
}: {
	source: HelpSource;
	pageKey: string;
	/** Staff only: go to a page linked from an article. */
	go?: (path: string) => void;
}) {
	const [open, setOpen] = useState<OpenHelp | null>(null);
	useEffect(() => {
		const onOpen = (e: Event) => setOpen((e as CustomEvent<OpenHelp>).detail ?? {});
		window.addEventListener("help:open", onOpen);
		return () => window.removeEventListener("help:open", onOpen);
	}, []);
	if (!open) return null;
	return (
		<Suspense
			fallback={
				// On a slow connection, say something is happening rather than nothing.
				<p
					role="status"
					className="fixed top-4 right-4 z-50 rounded-full bg-card px-4 py-3 font-semibold text-ink shadow-lg"
				>
					Opening help…
				</p>
			}
		>
			<HelpDrawer
				// A new "Help with this" while open starts the drawer at that article.
				key={open.slug ?? "page"}
				source={source}
				pageKey={pageKey}
				startAt={open.slug}
				go={go}
				onClose={() => setOpen(null)}
			/>
		</Suspense>
	);
}
